/**
 * Read-only legacy adapter: ghg_inventory_precalculated as one mutable snapshot per city-year.
 * Profile fields sourced from population, population_density, ccas, cbei_cities independently.
 */

import { supabaseClient } from '../../services/supabase';
import { FROZEN_UNIVERSE, buildGHGInventoryReport } from './buildGHGInventoryReport';
import { buildConsumptionInventoryFromCbei, type CbeiRawRow } from './cbeiReportScaling';
import { normalizeCountyName } from './countyNormalize';
import { getFrozen2023UniverseEntries, resolveByGeofips, toReportJurisdiction } from './crosswalk';
import { isPrototypeEligible2023 } from './eligibility';
import { fetchAllOrdered } from './pagination';
import { mapPrecalcToSectorObservations, type PrecalcRow } from './sectorMapping';
import type { BuildGHGInventoryReportInput, GeneratedGHGInventoryReport, HistoricalYearPoint } from './types';
import { numOrNull, reportValue } from './valueHelpers';

export interface InventoryReportAdapter {
  loadReport(geofips: number, year?: number): Promise<GeneratedGHGInventoryReport>;
}

const PRECALC_SELECT =
  'id,geofips,location_name,year,calculation_date,ldv_vmt,ldv_vmt_source,ldv_emission_factor,ldv_emission_factor_source,ldv_emissions,ldv_notes,hdv_emissions,hdv_data_source,elec_residential_kwh,elec_commercial_kwh,elec_carbon_intensity,elec_carbon_intensity_source,elec_provider,elec_residential_emissions,elec_commercial_emissions,elec_total_emissions,elec_residential_source,elec_commercial_source,ng_residential_therms,ng_commercial_therms,ng_residential_emissions,ng_commercial_emissions,ng_total_emissions,ng_residential_source,ng_commercial_source,waste_tons,waste_tons_source,waste_emission_factor,waste_emission_factor_source,waste_emissions,offroad_emissions,offroad_data_source,agriculture_emissions,total_emissions';

export async function fetchPrecalcRow(geofips: number, year: number): Promise<PrecalcRow | null> {
  const { data, error } = await supabaseClient
    .from('ghg_inventory_precalculated')
    .select(PRECALC_SELECT)
    .eq('geofips', geofips)
    .eq('year', year)
    .eq('geography_type', 'City')
    .order('id', { ascending: true })
    .limit(1)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data as PrecalcRow) ?? null;
}

export async function fetchPrecalcHistory(geofips: number): Promise<PrecalcRow[]> {
  const { data, error } = await supabaseClient
    .from('ghg_inventory_precalculated')
    .select('id,year,total_emissions,ldv_vmt_source,calculation_date')
    .eq('geofips', geofips)
    .eq('geography_type', 'City')
    .order('year', { ascending: true })
    .order('id', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as PrecalcRow[];
}

async function fetchPopulationRow(geofips: number, year: number) {
  const { data, error } = await supabaseClient
    .from('population')
    .select('population,location_name,county,geography_type')
    .eq('geofips', geofips)
    .eq('year', year)
    .order('geofips', { ascending: true })
    .limit(1)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data;
}

async function fetchDensityRow(geofips: number, year: number) {
  const { data, error } = await supabaseClient
    .from('population_density')
    .select('population_density,year')
    .eq('geofips', geofips)
    .eq('year', year)
    .order('year', { ascending: false })
    .order('geofips', { ascending: true })
    .limit(1)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data;
}

async function fetchCcasiRow(geofips: number) {
  const { data, error } = await supabaseClient
    .from('ccas')
    .select('geofips,load_serving_entity,previous_iou,cca_start_year')
    .eq('geofips', geofips)
    .order('geofips', { ascending: true })
    .limit(1)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data;
}

async function fetchCbeiRows(geofips: number): Promise<CbeiRawRow[]> {
  const { data, error } = await supabaseClient
    .from('cbei_cities')
    .select(
      'year,transportation_total_emissions,housing_total_emissions,food_total_emissions,goods_total_emissions,services_total_emissions,hhcount'
    )
    .eq('geofips', geofips)
    .order('year', { ascending: true })
    .order('geofips', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as CbeiRawRow[];
}

async function fetchRankingPool(year: number): Promise<BuildGHGInventoryReportInput['rankingPool']> {
  type Raw = {
    id: number;
    geofips: number;
    county_name: string | null;
    total_emissions: number | null;
    ldv_emissions: number | null;
    hdv_emissions: number | null;
    elec_residential_emissions: number | null;
    elec_commercial_emissions: number | null;
    elec_residential_kwh: number | null;
    elec_commercial_kwh: number | null;
    ng_residential_emissions: number | null;
    ng_commercial_emissions: number | null;
    waste_emissions: number | null;
    offroad_emissions: number | null;
  };
  const precalcRows = await fetchAllOrdered<Raw>(() =>
    supabaseClient
      .from('ghg_inventory_precalculated')
      .select(
        'id,geofips,county_name,total_emissions,ldv_emissions,hdv_emissions,elec_residential_emissions,elec_commercial_emissions,elec_residential_kwh,elec_commercial_kwh,ng_residential_emissions,ng_commercial_emissions,waste_emissions,offroad_emissions'
      )
      .eq('year', year)
      .eq('geography_type', 'City')
      .gte('geofips', 600000)
      .lt('geofips', 700000)
      .order('id', { ascending: true }) as unknown as {
      range: (f: number, t: number) => Promise<{ data: Raw[] | null; error: { message: string } | null }>;
    }
  );

  const byGeofips = new Map<number, Raw>();
  for (const r of precalcRows) byGeofips.set(r.geofips, r);

  type PopRow = { geofips: number; population: number | null; county: string | null };
  const popRows = await fetchAllOrdered<PopRow>(() =>
    supabaseClient
      .from('population')
      .select('geofips,population,county')
      .eq('year', year)
      .eq('geography_type', 'City')
      .order('geofips', { ascending: true }) as unknown as {
      range: (f: number, t: number) => Promise<{ data: PopRow[] | null; error: { message: string } | null }>;
    }
  );
  const popMap = new Map(popRows.map((p) => [p.geofips, p]));

  type DenRow = { geofips: number; population_density: number | null };
  const denRows = await fetchAllOrdered<DenRow>(() =>
    supabaseClient
      .from('population_density')
      .select('geofips,population_density')
      .eq('year', year)
      .order('geofips', { ascending: true }) as unknown as {
      range: (f: number, t: number) => Promise<{ data: DenRow[] | null; error: { message: string } | null }>;
    }
  );
  const denMap = new Map(denRows.map((d) => [d.geofips, d]));

  const pool: BuildGHGInventoryReportInput['rankingPool'] = [];
  for (const entry of getFrozen2023UniverseEntries()) {
    const geofips = entry.populationGeofips;
    const precalc = geofips != null ? byGeofips.get(geofips) : undefined;
    const pop = geofips != null ? popMap.get(geofips) : undefined;
    const den = geofips != null ? denMap.get(geofips) : undefined;
    pool.push({
      jurisdictionId: entry.stableJurisdictionId ?? `cap:${entry.capName}`,
      geofips,
      county: normalizeCountyName(pop?.county ?? entry.capCounty),
      totalMt: precalc ? numOrNull(precalc.total_emissions) : null,
      population: pop ? numOrNull(pop.population) : null,
      density: den ? numOrNull(den.population_density) : null,
      inFrozenUniverse: true,
      prototypeEligible: isPrototypeEligible2023(entry.stableJurisdictionId),
      inventoryYear: year,
      coreSectors: {
        ldv: precalc ? numOrNull(precalc.ldv_emissions) : null,
        hdv: precalc ? numOrNull(precalc.hdv_emissions) : null,
        elec_res: precalc ? numOrNull(precalc.elec_residential_emissions) : null,
        elec_com: precalc ? numOrNull(precalc.elec_commercial_emissions) : null,
        ng_res: precalc ? numOrNull(precalc.ng_residential_emissions) : null,
        ng_com: precalc ? numOrNull(precalc.ng_commercial_emissions) : null,
        waste: precalc ? numOrNull(precalc.waste_emissions) : null,
        offroad: precalc ? numOrNull(precalc.offroad_emissions) : null,
      },
      coreActivity: {
        elec_res_kwh: precalc ? numOrNull(precalc.elec_residential_kwh) : null,
        elec_com_kwh: precalc ? numOrNull(precalc.elec_commercial_kwh) : null,
      },
    });
  }
  return pool;
}

function formatUtilityProvider(ccas: { load_serving_entity?: string | null; previous_iou?: string | null; cca_start_year?: number | null } | null, year: number): string | null {
  if (!ccas) return null;
  const lse = ccas.load_serving_entity;
  const prev = ccas.previous_iou;
  const start = ccas.cca_start_year;
  if (start != null && year < start && prev) return `${prev} (IOU before CCA start ${start})`;
  if (lse) return String(lse);
  if (prev) return String(prev);
  return null;
}

export async function buildLegacyReportInput(geofips: number, year = 2023): Promise<BuildGHGInventoryReportInput> {
  const crosswalk = resolveByGeofips(geofips);
  if (!crosswalk) throw new Error(`No crosswalk entry for geofips ${geofips}`);

  const [precalc, history, population, density, ccas, cbei, rankingPool] = await Promise.all([
    fetchPrecalcRow(geofips, year),
    fetchPrecalcHistory(geofips),
    fetchPopulationRow(geofips, year),
    fetchDensityRow(geofips, year),
    fetchCcasiRow(geofips),
    fetchCbeiRows(geofips),
    fetchRankingPool(year),
  ]);

  const jurisdiction = toReportJurisdiction(crosswalk);
  const sectors = precalc ? mapPrecalcToSectorObservations(precalc) : [];

  const historicalPoints: HistoricalYearPoint[] = history.map((h) => ({
    year: h.year,
    totalEmissionsMt: reportValue(numOrNull(h.total_emissions), 'MT CO2e', h.total_emissions == null ? 'missing' : 'modeled', {
      sourceTable: 'ghg_inventory_precalculated',
      sourceColumn: 'total_emissions',
      sourceRowId: h.id,
    }),
    ldvSource: h.ldv_vmt_source ?? null,
  }));

  const pop = numOrNull(population?.population);
  const den = numOrNull(density?.population_density);
  const cbeiYear = cbei.find((r) => r.year === year);
  const hh = numOrNull(cbeiYear?.hhcount);
  const utility = formatUtilityProvider(ccas, year);

  return {
    jurisdiction,
    universe: FROZEN_UNIVERSE,
    inventoryRun: {
      inventoryRunId: precalc ? `legacy:${precalc.id}` : `legacy:missing:${geofips}:${year}`,
      inventoryYear: year,
      methodLabel: 'ClimatePlans legacy precalculated snapshot',
      methodVersion: null,
      legacySnapshot: {
        kind: 'legacy_precalc_row',
        table: 'ghg_inventory_precalculated',
        rowId: precalc?.id ?? -1,
        geofips,
        year,
        calculationDate: precalc?.calculation_date ?? null,
      },
      runStatus: 'legacy_snapshot',
      publicationStatus: 'provisional',
    },
    storedTotalMt: numOrNull(precalc?.total_emissions),
    populationForYear: reportValue(pop, 'residents', pop == null ? 'missing' : 'observed', {
      sourceTable: 'population',
      sourceColumn: 'population',
      sourceYear: year,
    }),
    households: hh != null
      ? reportValue(hh, 'households', 'modeled', {
          sourceTable: 'cbei_cities',
          sourceColumn: 'hhcount',
          sourceYear: year,
          calculationRule: 'CBEI model household count; not a Census ACS observation',
        })
      : reportValue(null, 'households', 'missing', {
          sourceTable: 'cbei_cities',
          sourceColumn: 'hhcount',
          sourceYear: year,
        }),
    populationDensity: den != null
      ? reportValue(den, 'residents/sq mi', 'observed', {
          sourceTable: 'population_density',
          sourceColumn: 'population_density',
          sourceYear: year,
        })
      : undefined,
    utilityProvider: utility
      ? reportValue(utility, '', 'modeled', {
          sourceTable: 'ccas',
          sourceColumn: 'load_serving_entity,previous_iou,cca_start_year',
          sourceYear: year,
          calculationRule: 'ccas row is not year-specific; treated as applicable for the inventory year, not observed in that year',
        })
      : undefined,
    sectorObservations: sectors,
    historicalPoints,
    consumption: buildConsumptionInventoryFromCbei(cbei, year),
    rankingPool,
  };
}

export const legacyInventoryReportAdapter: InventoryReportAdapter = {
  async loadReport(geofips: number, year = 2023) {
    const input = await buildLegacyReportInput(geofips, year);
    return buildGHGInventoryReport(input);
  },
};
